import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Trash2, Filter, AlertTriangle, ShieldOff } from "lucide-react";

const MODULE_TONE = {
  hard_delete_customer: "bg-red-500/15 text-red-300 border-red-500/30",
  hard_delete_loan: "bg-red-500/15 text-red-300 border-red-500/30",
  hard_delete_payment: "bg-red-500/15 text-red-300 border-red-500/30",
  hard_delete_shop: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  hard_delete_referral_partner: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  hard_delete_password_failed: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

const MODULE_LABEL = {
  hard_delete_customer: "Customer",
  hard_delete_loan: "Loan",
  hard_delete_payment: "Payment",
  hard_delete_shop: "Shop",
  hard_delete_referral_partner: "Referral Partner",
  hard_delete_password_failed: "Failed attempt",
};

const FILTERS = [
  { value: "", label: "All" },
  { value: "customer", label: "Customers" },
  { value: "loan", label: "Loans" },
  { value: "payment", label: "Payments" },
  { value: "shop", label: "Shops" },
  { value: "referral_partner", label: "Referral Partners" },
  { value: "password_failed", label: "Failed attempts" },
];

export default function AdminDeletions() {
  const [data, setData] = useState({ rows: [], summary: [] });
  const [module, setModule] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (mod) => {
    setLoading(true);
    try {
      const params = mod ? { module: mod } : {};
      const { data } = await api.get("/admin/activity/deletions", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(module); }, [module]);

  const summaryCard = (key) => {
    const row = data.summary.find((s) => s.event === `hard_delete_${key}`);
    return row?.count || 0;
  };

  return (
    <div className="space-y-6" data-testid="admin-deletions-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white flex items-center gap-2">
          <ShieldOff className="w-7 h-7 text-red-400" /> Permanent Deletions
        </h1>
        <p className="text-slate-400 mt-1">
          Every hard-delete from across the platform — who, when, what, and the cascade impact. Includes failed password attempts for security review.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { key: "customer", label: "Customers", emoji: "👤" },
          { key: "loan", label: "Loans", emoji: "📄" },
          { key: "payment", label: "Payments", emoji: "💰" },
          { key: "shop", label: "Shops", emoji: "🏪" },
          { key: "referral_partner", label: "Partners", emoji: "🤝" },
          { key: "password_failed", label: "Failed", emoji: "🔒" },
        ].map((c) => (
          <button
            key={c.key}
            onClick={() => setModule(c.key === "" ? "" : c.key)}
            data-testid={`deletion-tile-${c.key}`}
            className={`bg-[#151A23] border rounded-md p-4 text-left transition ${module === c.key ? "border-amber-500 ring-1 ring-amber-500/40" : "border-slate-800 hover:border-slate-700"}`}
          >
            <div className="text-xs text-slate-400 uppercase font-semibold">{c.emoji} {c.label}</div>
            <div className="font-display text-2xl font-bold text-white tabular mt-1">{summaryCard(c.key)}</div>
          </button>
        ))}
      </div>

      <div className="bg-[#151A23] border border-slate-800 rounded-md">
        <div className="p-4 border-b border-slate-800 flex flex-wrap items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          {FILTERS.map((f) => (
            <button
              key={f.value || "all"}
              onClick={() => setModule(f.value)}
              data-testid={`deletion-filter-${f.value || "all"}`}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${module === f.value ? "bg-amber-500 border-amber-500 text-slate-900" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}
            >
              {f.label}
            </button>
          ))}
          <div className="ml-auto text-xs text-slate-500">
            {loading ? "Loading…" : `${data.rows.length} entr${data.rows.length === 1 ? "y" : "ies"}`}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">When</th>
                <th className="text-left px-4 py-3 font-semibold">Module</th>
                <th className="text-left px-4 py-3 font-semibold">Target</th>
                <th className="text-left px-4 py-3 font-semibold">Actor</th>
                <th className="text-left px-4 py-3 font-semibold">IP</th>
                <th className="text-left px-4 py-3 font-semibold">Cascade</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No permanent deletions logged yet.</td></tr>
              )}
              {data.rows.map((r) => {
                const tone = MODULE_TONE[r.event] || "border-slate-700 text-slate-300";
                const label = MODULE_LABEL[r.event] || r.event.replace("hard_delete_", "");
                const ip = r.meta?.ip || "—";
                const cascade = r.meta?.cascade || {};
                const cascadeStr = Object.entries(cascade)
                  .filter(([, v]) => v !== 0 && v !== "" && v !== null)
                  .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
                  .join(" · ") || "—";
                return (
                  <tr key={r.id} className="border-t border-slate-800" data-testid={`deletions-row-${r.id}`}>
                    <td className="px-4 py-3 text-xs tabular text-slate-400 whitespace-nowrap">
                      {(r.created_at || "").slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold border ${tone}`}>
                        {r.event === "hard_delete_password_failed" ? <AlertTriangle className="w-3 h-3" /> : <Trash2 className="w-3 h-3" />}
                        {label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white">{r.target_label || r.target_id?.slice(0, 8) || "—"}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs tabular">
                      <div>{r.actor_email}</div>
                      <div className="text-[10px] text-slate-500 uppercase">{r.actor_role}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs tabular">{ip}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-md truncate" title={cascadeStr}>{cascadeStr}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
